import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertCircle,
  CarFront,
  CreditCard,
  DollarSign,
  FileText,
  Home,
  Loader2,
  Plug,
} from "lucide-react";
import { useRef, useState } from "react";
import { ContactOrganization } from "@/components/contact-organization";
import { FileUploadField } from "@/components/file-upload-field";
import { PageHeader } from "@/components/page-header";
import { PageShell } from "@/components/page-shell";
import { Section } from "@/components/section";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldContent,
  FieldGroup,
  FieldTitle,
} from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type DocumentType,
  documentTypesQueryOptions,
  ensureConsumerQueryData,
  formOptionsQueryOptions,
  useCreateDocumentMutation,
} from "@/lib/data";
import { requireSession } from "@/lib/session";
import { validateUploadedFile } from "@/lib/uploads";
import { handleApiError, labelFor } from "@/lib/utils";

const CATEGORY_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  identity: CreditCard,
  income: DollarSign,
  residence: Home,
  utility_bill: Plug,
  auto_insurance: CarFront,
  other: FileText,
};

export const Route = createFileRoute("/documents_/new")({
  component: DocumentNewPage,
  validateSearch: (search: Record<string, unknown>) => ({
    category: (search.category as string) || "other",
  }),
  beforeLoad: requireSession,
  loader: () => ensureConsumerQueryData(documentTypesQueryOptions),
});

function DocumentNewPage() {
  const { category } = Route.useSearch();
  const navigate = useNavigate();
  const { data: docTypes } = useQuery(documentTypesQueryOptions);
  const { data: formOptions } = useQuery(formOptionsQueryOptions);
  const createDocument = useCreateDocumentMutation();

  const filteredTypes = (docTypes ?? []).filter(
    (t: DocumentType) => t.category === category,
  );

  const [chosenTypeId, setChosenTypeId] = useState("");
  // With only one type to pick from, it is picked for the applicant.
  const selectedTypeId =
    chosenTypeId || (filteredTypes.length === 1 ? filteredTypes[0].id : "");
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [typeError, setTypeError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const categoryLabel = labelFor(
    formOptions?.document_categories,
    category,
    category,
  );
  const CategoryIcon = CATEGORY_ICONS[category] ?? FileText;

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0] ?? null;
    setFileError(null);
    if (selected) {
      const validationError = validateUploadedFile(selected);
      if (validationError) {
        setFileError(validationError);
        e.target.value = "";
        setFile(null);
        return;
      }
    }
    setFile(selected);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setServerError(null);
    setFileError(null);
    setTypeError(null);

    if (!file) {
      setFileError("Please select a file to upload");
      return;
    }
    if (!selectedTypeId) {
      setTypeError("Please select a document type");
      return;
    }

    const formData = new FormData();
    formData.append("document[attachment]", file);
    formData.append("document[uploaded_document_type_id]", selectedTypeId);

    try {
      await createDocument.mutateAsync(formData);
      void navigate({ to: "/documents" });
    } catch (err: unknown) {
      setServerError(await handleApiError(err));
    }
  }

  return (
    <PageShell>
      <PageHeader
        eyebrow={
          <span className="inline-flex items-center gap-1.5">
            <CategoryIcon className="size-3.5" />
            {categoryLabel}
          </span>
        }
        title={`Upload ${categoryLabel.toLowerCase()} document`}
        description="Clear photos or a PDF work best. The dealer uses this to verify your file."
        backTo="/documents"
        backLabel="Back to documents"
      />

      <form onSubmit={handleSubmit}>
        <FieldGroup>
          {docTypes !== undefined && filteredTypes.length === 0 && (
            <div className="rounded-xl border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
              <p>
                No document types are set up for this category yet. Go back and
                pick a different category.
              </p>
              <ContactOrganization>
                Or contact the dealer for help.
              </ContactOrganization>
            </div>
          )}
          {filteredTypes.length > 1 && (
            <Section title="Document type">
              <Field data-invalid={!!typeError}>
                <FieldTitle>Which document is this?</FieldTitle>
                <FieldContent>
                  <Select
                    value={selectedTypeId}
                    onValueChange={setChosenTypeId}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select document type" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredTypes.map((type: DocumentType) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {typeError && (
                    <p className="flex animate-in items-center gap-1.5 text-sm text-destructive fade-in slide-in-from-top-1">
                      <AlertCircle className="size-3.5 shrink-0" />
                      {typeError}
                    </p>
                  )}
                </FieldContent>
              </Field>
            </Section>
          )}

          <Section
            title="File"
            description="Take a photo or choose a file from your device."
          >
            <FileUploadField
              file={file}
              error={fileError}
              inputRef={fileInputRef}
              onChange={handleFileChange}
              onClear={() => {
                setFile(null);
                setFileError(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
              promptDesktop="Click to add a document"
            />
          </Section>

          {serverError && (
            <div className="flex animate-in items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/8 p-3 text-sm text-destructive fade-in">
              <AlertCircle className="mt-0.5 size-4 shrink-0" />
              <span>{serverError}</span>
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            size="lg"
            disabled={
              createDocument.isPending ||
              (docTypes !== undefined && filteredTypes.length === 0)
            }
          >
            {createDocument.isPending ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                Uploading...
              </>
            ) : (
              "Upload document"
            )}
          </Button>
        </FieldGroup>
      </form>
    </PageShell>
  );
}
